import React from 'react';
import { fromJS } from 'immutable';
import styles from './styles.scss';
import moment from 'moment';
import { TicketCheckoutSummaryForm } from '../Summary';

/**
 * AddonsCheckoutController
 * Displays event add-ons as workshop cards with time selection
 * Shows workshop name, available times, and checkmark when selected
 */
const AddonsCheckoutController = ({
  addons,
  addonConfig,
  selectedAddons,
  onAddonSelectionChange,
  onResizeComponent,
  tickets,
  event,
  cart,
  settings,
  checkoutValues,
  loadingCheckoutValues,
  ticketOverrides,
  seatSelections,
}) => {
  if (!addons || addons.size === 0) {
    return null;
  }

  // Check if max limit reached
  const maxReached = addonConfig && selectedAddons.size >= (addonConfig.max_addons || 999);

  // Check if an addon is selected
  const isAddonSelected = (addonId, occurrenceId = null) =>
    selectedAddons.some((selection) => {
      const s = selection.toJS ? selection.toJS() : selection;
      if (occurrenceId) {
        return s.event_addon_id === addonId && s.occurrence_id === occurrenceId;
      }
      return s.event_addon_id === addonId;
    });

  // Handle addon selection
  const handleAddonSelect = (addon, ticketId, occurrenceId = null) => {
    const addonData = addon.toJS ? addon.toJS() : addon;
    const addonId = addonData.event_addon_id;

    // Check if already selected
    const existingIndex = selectedAddons.findIndex((s) => {
      const selection = s.toJS ? s.toJS() : s;
      if (occurrenceId) {
        return selection.event_addon_id === addonId && selection.occurrence_id === occurrenceId;
      }
      return selection.event_addon_id === addonId;
    });

    if (existingIndex !== -1) {
      // Deselect - remove from list
      const newSelections = selectedAddons.delete(existingIndex);
      onAddonSelectionChange(newSelections);
      onResizeComponent && onResizeComponent();
      return;
    }

    // Check max limit - don't allow selection
    if (addonConfig && selectedAddons.size >= (addonConfig.max_addons || 999)) {
      return; // Silently ignore - card should be disabled
    }

    // Get the occurrence if time-based
    let occurrence = null;
    if (occurrenceId) {
      // First try to find in direct occurrences
      occurrence = addonData.occurrences.find((o) => o.id === occurrenceId);

      // If not found, look in time_entries within occurrences
      if (!occurrence) {
        addonData.occurrences.some((occ) => {
          if (occ.time_entries && occ.time_entries.length > 0) {
            occurrence = occ.time_entries.find((te) => te.id === occurrenceId);
            return !!occurrence; // Return true to break the loop
          }
          return false;
        });
      }
    }

    // Get the ticket
    const ticket = addonData.tickets && addonData.tickets.length > 0 ? addonData.tickets[0] : null;

    // Add selection
    const newSelection = {
      event_addon_id: addonId,
      addon_event_id: occurrenceId || addonData.addon_id, // Use occurrence ID if time-based, otherwise parent ID
      ticket_id: ticket ? ticket.id : null,
      occurrence_id: occurrenceId,
      addon: addonData,
      ticket,
      occurrence,
    };

    onAddonSelectionChange(selectedAddons.push(fromJS(newSelection)));
    onResizeComponent && onResizeComponent();
  };

  // Render time slots for addon with occurrences
  const renderTimeSlots = (addon) => {
    const addonData = addon.toJS ? addon.toJS() : addon;
    const occurrences = addonData.occurrences || [];

    if (occurrences.length === 0) return null;

    // Check if this is a time-based event with time_entries
    const hasTimeEntries = occurrences.some((occ) => occ.time_entries && occ.time_entries.length > 0);

    if (hasTimeEntries) {
      // Time-based with time_entries: Show date headers with times underneath
      return (
        <div className={styles.timeSlotsContainer}>
          {occurrences.map((occurrence) => {
            if (!occurrence.time_entries || occurrence.time_entries.length === 0) return null;

            return (
              <div key={occurrence.id || occurrence.start_date} className={styles.dateGroup}>
                <div className={styles.dateHeader}>{moment(occurrence.start_date).format('ddd, MMM D, YYYY')}</div>
                <div className={styles.timeSlots}>
                  {occurrence.time_entries.map((timeEntry) => {
                    const isSelected = isAddonSelected(addonData.event_addon_id, timeEntry.id);
                    const isDisabled = maxReached && !isSelected;
                    return (
                      <div
                        key={timeEntry.id}
                        className={`${styles.timeSlot} ${isSelected ? styles.timeSlotSelected : ''} ${isDisabled ? styles.timeSlotDisabled : ''}`}
                        onClick={() => !isDisabled && handleAddonSelect(addon, addonData.tickets[0]?.id, timeEntry.id)}
                      >
                        <span className={styles.timeSlotTime}>
                          {moment(timeEntry.start_date).format('h:mm A')} - {moment(timeEntry.end_date).format('h:mm A')}
                        </span>
                        {isSelected && <span className={styles.checkmark}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    // Multi-day or recurring: Just show date/time selections
    return (
      <div className={styles.timeSlotsContainer}>
        <div className={styles.timeSlots}>
          {occurrences.map((occ) => {
            const isSelected = isAddonSelected(addonData.event_addon_id, occ.id);
            const isDisabled = maxReached && !isSelected;
            const startMoment = moment(occ.start_date);
            const endMoment = moment(occ.end_date);

            // Format based on whether it's same day or multi-day
            const isSameDay = startMoment.format('YYYY-MM-DD') === endMoment.format('YYYY-MM-DD');
            const displayText = isSameDay
              ? `${startMoment.format('ddd, MMM D, h:mm A')}`
              : `${startMoment.format('ddd, MMM D')} - ${endMoment.format('ddd, MMM D')}`;

            return (
              <div
                key={occ.id}
                className={`${styles.timeSlot} ${isSelected ? styles.timeSlotSelected : ''} ${isDisabled ? styles.timeSlotDisabled : ''}`}
                onClick={() => !isDisabled && handleAddonSelect(addon, addonData.tickets[0]?.id, occ.id)}
              >
                <span className={styles.timeSlotTime}>{displayText}</span>
                {isSelected && <span className={styles.checkmark}>✓</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.addonsCheckoutWrapper}>
      <div className={styles.addonsContent}>
        <div className={styles.addonsHeader}>
          <h2>{settings.get('addonSelectionHeaderText')}</h2>
          {addonConfig && (
            <p className={styles.addonsSubtitle}>
              {addonConfig.min_addons > 0
                ? `Select ${addonConfig.min_addons} to ${addonConfig.max_addons} ${settings.get('addonSelectionRequiredText')}`
                : `Select up to ${addonConfig.max_addons} ${settings.get('addonSelectionOptionalText')}`}
            </p>
          )}
          {maxReached && (
            <p className={styles.maxReachedMessage}>
              Maximum of {addonConfig.max_addons} {settings.get('addonMaxReachedText')}
            </p>
          )}
        </div>

        <div className={styles.addonsList}>
          {addons.map((addon) => {
            const addonData = addon.toJS ? addon.toJS() : addon;
            const hasOccurrences = addonData.occurrences && addonData.occurrences.length > 0;
            const isSelected = isAddonSelected(addonData.event_addon_id);
            const ticket = addonData.tickets && addonData.tickets.length > 0 ? addonData.tickets[0] : null;

            return (
              <div key={addonData.event_addon_id} className={styles.addonCard}>
                <div className={styles.addonCardHeader}>
                  <div className={styles.addonCardTitle}>
                    <h3>{addonData.title}</h3>
                    {isSelected && !hasOccurrences && <span className={styles.checkmarkBadge}>✓</span>}
                  </div>
                  <div className={styles.addonCardPrice}>
                    {(() => {
                      if (ticket && ticket.is_free) return 'Included';
                      if (ticket) return `$${ticket.price.toFixed(2)}`;
                      return 'Free';
                    })()}
                  </div>
                </div>

                {addonData.description && <p className={styles.addonCardDescription}>{addonData.description}</p>}

                {/* Show time slots if there are occurrences (time-based, multi-day, or recurring) */}
                {hasOccurrences ? (
                  renderTimeSlots(addon)
                ) : (
                  <div className={styles.timeSlotsContainer}>
                    <div className={styles.timeSlots}>
                      <div
                        className={`${styles.timeSlot} ${styles.timeSlotFullWidth} ${isSelected ? styles.timeSlotSelected : ''} ${
                          maxReached && !isSelected ? styles.timeSlotDisabled : ''
                        }`}
                        onClick={() => !(maxReached && !isSelected) && handleAddonSelect(addon, ticket?.id)}
                      >
                        <span className={styles.timeSlotTime}>{isSelected ? 'Selected' : 'Select'}</span>
                        {isSelected && <span className={styles.checkmark}>✓</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary on the same page */}
      <div className={styles.addonsSummary}>
        <TicketCheckoutSummaryForm
          loadingCheckoutValues={loadingCheckoutValues}
          checkoutValues={checkoutValues}
          tickets={tickets}
          event={event}
          cart={cart}
          settings={settings}
          ticketOverrides={ticketOverrides}
          seatSelections={seatSelections}
          selectedAddons={selectedAddons}
        />
      </div>
    </div>
  );
};

export default AddonsCheckoutController;
